import Sidebar from 'components/layout/Sidebar';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import styled from 'styled-components';
import PrintHeader from '../components/PrintHeader';
import { formatShopInfo } from '../utils';
import shopSelectors from 'services/shop/selectors';
import CashDepositReportPrintPreview from './CashDepositReportPrintPreview';
import reportApis from 'features/report/services/apis';
import { AxiosResponse } from 'axios';
import { ICashDepositPrintByDate, ICashDepositPrintById } from 'features/report/services/types/cash-deposit';
import moment from 'moment';
import { DATE_FORMAT_VIEW } from 'features/appointment/utils/format';

interface ICashDepositReportPrintPage {}

const CashDepositReportPrintPage: React.FC<ICashDepositReportPrintPage> = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [dataCashDepositReport, setDataCashDepositReport] = useState<ICashDepositPrintByDate | null>(null);
  const shopInfo = shopSelectors.data.shopInfo();
  const shopInfoFormatted = formatShopInfo(shopInfo);
  const allSetting = shopSelectors.data.allSetting();
  const v2 = allSetting?.versionUI === 'V2';

  const urlStartDate = searchParams.get('startDate') || '';
  const urlEndDate = searchParams.get('endDate') || '';
  const staffId = searchParams.get('staffId') || undefined;
  const cashDepositId = searchParams.get('cashDepositId') || undefined;
  const [displayStartDate, setDisplayStartDate] = useState(urlStartDate);
  const [displayEndDate, setDisplayEndDate] = useState(urlEndDate);
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    if (hasFetchedRef.current) return;

    // Single cash deposit print mode
    if (cashDepositId) {
      const fetchSingle = async () => {
        try {
          const res: AxiosResponse<{ data: ICashDepositPrintById }> = await reportApis.getPrintCashDepositById(cashDepositId);
          const item = res?.data?.data;
          if (item) {
            const dateKey = moment(item.createdDate, 'MM/DD/YYYY hh:mm A').format(DATE_FORMAT_VIEW);
            setDataCashDepositReport({
              [dateKey]: [item],
            });
            setDisplayStartDate(dateKey);
            setDisplayEndDate(dateKey);
          } else {
            setDataCashDepositReport(null);
          }
        } catch {
          setDataCashDepositReport(null);
        }
      };
      hasFetchedRef.current = true;
      fetchSingle();
      return;
    }

    // Range print mode
    if (!urlStartDate || !urlEndDate) return;
    const fetchData = async () => {
      try {
        const res: AxiosResponse<{ data: ICashDepositPrintByDate }> = await reportApis.getPrintCashDepositByDate({
          startDate: urlStartDate,
          endDate: urlEndDate,
          staffId,
        });
        if (res?.data?.data) {
          setDataCashDepositReport(res.data.data);
          setDisplayStartDate(urlStartDate);
          setDisplayEndDate(urlEndDate);
        } else {
          setDataCashDepositReport(null);
        }
      } catch {
        setDataCashDepositReport(null);
      }
    };
    hasFetchedRef.current = true;
    fetchData();
  }, [urlStartDate, urlEndDate, staffId, cashDepositId]);

  const onBack = () => {
    navigate(-1);
  };

  const divRef = useRef<HTMLDivElement>(null);
  const onPrint = useReactToPrint({
    content: () => divRef.current,
    copyStyles: true,
  });

  return (
    <div style={{ display: 'flex' }}>
      <Sidebar />
      <ContainerStyled>
        <PrintHeader v2={v2} onBack={onBack} onPrint={onPrint} />
        <div ref={divRef}>
          <CashDepositReportPrintPreview
            data={dataCashDepositReport}
            startDate={displayStartDate}
            endDate={displayEndDate}
            shopInfo={shopInfoFormatted}
          />
        </div>
      </ContainerStyled>
    </div>
  );
};

export default CashDepositReportPrintPage;

const ContainerStyled = styled.div`
  background-color: #e5e5e5;
  height: 100vh;
  position: relative;
  flex-grow: 1;
  width: 100%;
  overflow: auto;
`;
